import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import { TbSearch } from 'react-icons/tb';
import { adminApi as defaultAdminApi } from './admin-api';
import { generateTemporaryPassword } from '../lib/temporary-password';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { formatDocumentNumber } from '../lib/document-number';
import { MemberForm } from './MemberForm';
import { TemporaryPasswordNotice } from './TemporaryPasswordNotice';
import { ConfirmationSheet } from '../components/ConfirmationSheet';
import './admin.css';

const TITLE = 'Socios';
const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
];

function normalize(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function matchesSearch(member, term) {
  if (!term) return true;
  if (normalize(member.username).includes(term) || normalize(member.email).includes(term)) return true;
  const digits = term.replace(/[^0-9]/g, '');
  const documentNumber = member.profile && member.profile.documentNumber;
  return Boolean(digits) && Boolean(documentNumber) && documentNumber.includes(digits);
}

function matchesFilter(member, filter) {
  if (filter === 'active') return member.isActive;
  if (filter === 'inactive') return !member.isActive;
  return true;
}

/**
 * `/admin/socios` (spec "Administrador": "Socios consume /members. Permite
 * búsqueda local por nombre/email, filtrar activos/inactivos, crear una
 * cuenta y entrar al detalle"). `GET /members` is fetched exactly once per
 * mount; search and the active/inactive filter run entirely in memory over
 * that same array — never a second request. The search box and filter
 * toggles are always rendered (not gated behind the list's own
 * loading/error/empty state) so they stay usable, and testable, the instant
 * the page mounts.
 *
 * "Nuevo socio" only appears once the list is `ready` AND no temporary
 * password notice is still open (fix round 1, Codex review): starting it
 * while `GET /members` is still in flight let a still-pending, now-stale
 * response overwrite a member that had just been created (`handleCreated`
 * bailed out on `prev.members === null`, and the late `.then()` in `load()`
 * would replace state with a list that predates the creation either way);
 * starting a second one before closing the first notice silently replaced
 * `createdPassword`, discarding a temporary password the admin hadn't
 * copied yet. Gating the action on `ready` removes the race instead of
 * trying to merge/dedupe against a late response.
 */
export function MembersPage({ api = defaultAdminApi, generatePassword = generateTemporaryPassword }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', members: null, message: '' });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);
  const [documentPasswordDialogOpen, setDocumentPasswordDialogOpen] = useState(false);
  const [documentPasswordResult, setDocumentPasswordResult] = useState(null);
  const [documentPasswordError, setDocumentPasswordError] = useState('');
  const [documentPasswordSubmitting, setDocumentPasswordSubmitting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportingFormat, setExportingFormat] = useState(null);

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', members: null, message: '' });

    api.listMembers()
      .then((members) => {
        if (!active) return;
        setState({ status: 'ready', members, message: '' });
      })
      .catch((error) => {
        if (!active) return;
        const clearBusinessData = () => setState({ status: 'loading', members: null, message: '' });
        const outcome = handleApiError(error, { clearBusinessData });
        if (outcome.handled) return;
        setState({ status: 'error', members: null, message: errorMessageFor(error) });
      });

    return () => {
      active = false;
    };
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  const visibleMembers = useMemo(() => {
    if (!state.members) return [];
    const term = normalize(search);
    return state.members.filter((member) => matchesFilter(member, filter) && matchesSearch(member, term));
  }, [state.members, search, filter]);

  function handleCreated({ user: createdUser, profile, password, passwordIsDocument }) {
    setState((prev) => {
      if (!prev.members) return prev;
      const row = {
        id: createdUser.id,
        username: createdUser.username,
        email: createdUser.email,
        role: createdUser.role,
        isActive: createdUser.isActive,
        mustChangePassword: createdUser.mustChangePassword,
        profile: profile || null,
      };
      return { ...prev, members: [row, ...prev.members] };
    });
    setCreatedPassword({ username: createdUser.username, password, passwordIsDocument });
    setFormOpen(false);
  }

  // Both buttons share this: a failure (network, 403...) shows a small inline
  // message without touching the list itself — same "a mutation/side-effect
  // failing never wipes already-loaded data" rule as the rest of this tree.
  // A 401 still goes through `handleApiError` first (session cleanup, then
  // redirect), same as every other request on this page.
  async function handleExport(format) {
    setExportError('');
    setExportingFormat(format);
    try {
      await api.exportMembers(format);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setExportError(errorMessageFor(caught));
    } finally {
      setExportingFormat(null);
    }
  }

  async function handleSetDocumentPasswords() {
    setDocumentPasswordError('');
    setDocumentPasswordResult(null);
    setDocumentPasswordSubmitting(true);
    try {
      setDocumentPasswordResult(await api.setMemberPasswordsToDocument());
      setDocumentPasswordDialogOpen(false);
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setDocumentPasswordError(errorMessageFor(caught));
    } finally {
      setDocumentPasswordSubmitting(false);
    }
  }

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header gym-admin-members__header">
        <div className="gym-admin-members__title-wrap">
          <h1 id="gym-page-title" className="gym-page__title">{TITLE}</h1>
          {state.status === 'ready' && <span className="gym-admin-members__count">{state.members.length} {state.members.length === 1 ? 'socio' : 'socios'}</span>}
        </div>
        <div className="gym-admin-members__header-actions">
          {state.status === 'ready' && !formOpen && !createdPassword && (
            <button type="button" className="gym-button gym-button--primary gym-admin-members__new" onClick={() => setFormOpen(true)}>
              Nuevo socio
            </button>
          )}
          {state.status === 'ready' && !formOpen && !createdPassword && (
            <Link to="/admin/socios/importar" className="gym-button gym-button--secondary">
              Importar socios
            </Link>
          )}
          {state.status === 'ready' && !formOpen && !createdPassword && (
            <Link to="/admin/socios/importar-pagos" className="gym-button gym-button--secondary">
              Importar historial de pagos
            </Link>
          )}
          <button type="button" className="gym-button gym-button--secondary" disabled={!!exportingFormat} onClick={() => handleExport('xlsx')}>
            {exportingFormat === 'xlsx' ? 'Exportando…' : 'Exportar Excel'}
          </button>
          <button type="button" className="gym-button gym-button--secondary" disabled={!!exportingFormat} onClick={() => handleExport('csv')}>
            {exportingFormat === 'csv' ? 'Exportando…' : 'Exportar CSV'}
          </button>
          {state.status === 'ready' && !formOpen && !createdPassword && (
            <button type="button" className="gym-button gym-button--danger" onClick={() => { setDocumentPasswordError(''); setDocumentPasswordResult(null); setDocumentPasswordDialogOpen(true); }}>
              Poner el DNI como contraseña
            </button>
          )}
        </div>
      </header>

      {exportError && <p role="alert" className="gym-form__error">{exportError}</p>}
      {documentPasswordError && !documentPasswordDialogOpen && <p role="alert" className="gym-form__error">{documentPasswordError}</p>}
      {documentPasswordResult && (
        <p role="status">
          Actualizados: {documentPasswordResult.updated}; sin DNI: {documentPasswordResult.skippedWithoutDocument}; ya elegida: {documentPasswordResult.skippedAlreadyChosen}
        </p>
      )}
      {documentPasswordDialogOpen && (
        <ConfirmationSheet
          title="Confirmar contraseña con DNI"
          description="Se cambiará la contraseña de socios activos que todavía no eligieron su contraseña y tienen DNI cargado."
          confirmLabel="Confirmar"
          pendingLabel="Actualizando…"
          busy={documentPasswordSubmitting}
          onConfirm={handleSetDocumentPasswords}
          onClose={() => setDocumentPasswordDialogOpen(false)}
          error={documentPasswordError}
        />
      )}

      {createdPassword && (
        <TemporaryPasswordNotice
          username={createdPassword.username}
          password={createdPassword.password}
          passwordIsDocument={createdPassword.passwordIsDocument}
          onClose={() => setCreatedPassword(null)}
        />
      )}

      {state.status === 'ready' && formOpen && (
        <MemberForm
          api={api}
          generatePassword={generatePassword}
          onCreated={handleCreated}
          onCancel={() => setFormOpen(false)}
        />
      )}

      <div className="gym-admin-members__controls">
        <label className="gym-admin-members__search-wrap">
          <TbSearch aria-hidden="true" focusable="false" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por nombre, email o DNI"
          aria-label="Buscar socios"
          className="gym-input gym-admin-members__search"
        />
        </label>
        <div className="gym-admin-members__filters" role="group" aria-label="Filtrar por estado">
          {FILTERS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={filter === value ? 'gym-admin-filter gym-admin-filter--active' : 'gym-admin-filter'}
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {state.status === 'loading' && (
        <div className="gym-state gym-state--loading" role="status" aria-live="polite">
          <span className="gym-state__spinner" aria-hidden="true" />
          <p className="gym-state__text">Cargando…</p>
        </div>
      )}

      {state.status === 'error' && (
        <div className="gym-state gym-state--error" role="alert">
          <p className="gym-state__text">{state.message}</p>
          <div className="gym-state__action">
            <button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>
          </div>
        </div>
      )}

      {state.status === 'ready' && state.members.length === 0 && (
        <div className="gym-state gym-state--empty">
          <p className="gym-state__text">Todavía no hay socios cargados.</p>
        </div>
      )}

      {state.status === 'ready' && state.members.length > 0 && visibleMembers.length === 0 && (
        <p className="gym-state__text">No hay socios que coincidan con la búsqueda.</p>
      )}

      {state.status === 'ready' && visibleMembers.length > 0 && (
        <table className="gym-table gym-admin-members-table">
          <caption className="gym-sr-only">Listado de socios</caption>
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Email</th>
              <th scope="col">DNI</th>
              <th scope="col">Estado</th>
              <th scope="col"><span className="gym-sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            {visibleMembers.map((member) => (
              <tr key={member.id}>
                <td data-label="Nombre">{member.username}</td>
                <td data-label="Email">{member.email}</td>
                <td data-label="DNI">
                  {member.profile && member.profile.documentNumber
                    ? formatDocumentNumber(member.profile.documentNumber)
                    : <span className="gym-muted-text">—</span>}
                </td>
                <td data-label="Estado">
                  <span className={member.isActive ? 'gym-badge gym-badge--success' : 'gym-badge gym-badge--neutral'}>
                    {member.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td data-label="Acciones">
                  <Link to={`/admin/socios/${member.id}`} className="gym-admin-members-link gym-admin-members-link--row-hit-area">Ver detalle</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
